/* eslint-disable react-hooks/exhaustive-deps */
import React, {useContext, useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import LogsLayout from './LogsLayout';
import {
  EmptyState,
  Loader,
  PageLimit,
  Pagination,
  PrimaryButton,
  SecondaryButton,
  TableLayout,
} from 'components';
import {eye} from 'assets/images/images';
import AuditLogsModal from 'routes/Settings/AuditLogs/AuditLogsModal';
import * as Actions from '../../store/actions';
import {useNavigate} from 'react-router';
import {PageContext} from 'helpers/context';
import {parseUrlParams} from 'helpers';
import moment from 'moment';
import FilterV2 from 'components/FilterV2';
import PageNumber from 'components/PageNumber';
import {dateRanges, formatDate} from 'helpers/getDateRange';

function AuditLogs({
  auth: {auditTrail, loading, userDetails},
  apps: {mAppLoading},
  getAuditTrails,
  exportAuditLogs,
  getTeamMembers,
  team: {members},
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(true);
  const [auditData, setAuditData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const {setPageName} = useContext(PageContext);

  useEffect(() => {
    setPageName('auditlogs');
  }, [setPageName]);

  useEffect(() => {
    getTeamMembers(`status=active`);
  }, [getTeamMembers]);

  const apiParams = parseUrlParams();

  useEffect(() => {
    if (!window.location.search && Object.keys(apiParams).length === 0) {
      return;
    }

    const paramsForAPI = {
      ...apiParams,
      limit,
      page: currentPage,
    };
    getAuditTrails(paramsForAPI);
  }, [currentPage, limit, getAuditTrails, JSON.stringify(apiParams)]);

  useEffect(() => {
    if (!window.location.search) {
      const searchParams = new URLSearchParams();
      const {start, end} = dateRanges.seven_days();

      searchParams.set('date', 'seven_days');
      searchParams.set('start', start);
      searchParams.set('end', end);

      navigate(`?${searchParams.toString()}`, {replace: true});
    }
  }, []);

  const pageClick = selected => {
    setCurrentPage(selected);
    const searchParams = new URLSearchParams(document.location.search);
    searchParams.set('page', selected);
    navigate(`?${searchParams.toString()}`);
  };

  const canDownloadAudit = useMemo(
    () =>
      userDetails?.permissions?.settingsPermissions?.viewAndDownloadAudit ||
      false,
    [userDetails],
  );

  const filterOptions = useMemo(() => {
    if (!members) {
      return [];
    }

    const formattedTeam =
      members?.team?.map(member => {
        const {first_name, last_name, email} = member?.user || {};
        const label =
          first_name && last_name
            ? `${first_name} ${last_name}`
            : email || String(member?.user?._id);
        return {
          value: member?.user?._id,
          label,
        };
      }) || [];

    return [
      {
        key: 'user',
        label: 'Member',
        type: 'select',
        options: formattedTeam,
      },
    ];
  }, [members]);

  const handleFilterChange = newFilters => {
    const filteredOptions = newFilters;
    if (filteredOptions.dateRange) {
      filteredOptions.start = formatDate(
        new Date(filteredOptions.dateRange.start),
      );
      filteredOptions.end = formatDate(new Date(filteredOptions.dateRange.end));
      delete filteredOptions.dateRange;
    }
    if (filteredOptions.user) {
      filteredOptions.user = Array.isArray(filteredOptions.user)
        ? filteredOptions.user.join(',')
        : filteredOptions.user;
    }
    setCurrentPage(1);
    const searchParams = new URLSearchParams();
    Object.entries(filteredOptions).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
    navigate(`?${searchParams.toString()}`);
    getAuditTrails({page: 1, limit, ...filteredOptions});
  };

  const isFiltered =
    !loading &&
    auditTrail?.records?.length === 0 &&
    Object.keys(parseUrlParams()).length > 2;

  return (
    <LogsLayout>
      <div className="flex items-center justify-end min-h-[52px] py-2 flex-wrap gap-3 mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <SecondaryButton
          buttonText="Filter"
          onClick={() => setOpenFilter(prev => !prev)}
        />
        <PrimaryButton
          disabled={mAppLoading || !canDownloadAudit}
          buttonText="Download"
          onClick={() => {
            const apiParams = parseUrlParams();
            apiParams.limit = limit;
            exportAuditLogs(apiParams.user);
          }}
          loading={mAppLoading}
        />
      </div>

      <div className={`filter-slide-container ${openFilter ? 'open' : ''}`}>
        <FilterV2
          filters={filterOptions}
          onFilterChange={handleFilterChange}
          initialValues={apiParams}
        />
      </div>

      <AuditLogsModal selectedLog={auditData} open={open} setOpen={setOpen} />

      {loading ? (
        <Loader />
      ) : !auditTrail?.records?.length && !isFiltered ? (
        <EmptyState noBtn height={55} body="No logs yet" />
      ) : !auditTrail?.records?.length && isFiltered ? (
        <EmptyState
          body="This filter did not return any data, try using different values."
          noBtn
          height={55}
          customBtn={
            <button
              onClick={() => {
                navigate('');
                setCurrentPage(1);
                getAuditTrails({limit, page: 1});
              }}
              className="text-brandBlue p-4 text-sm font-medium"
            >
              Update preferences
            </button>
          }
        />
      ) : (
        <>
          <TableLayout negativeMargins>
            <thead className="font-semibold uppercase">
              <tr className="bg-white80">
                <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>Member</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>Activity</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>Date</span>
                  </div>
                </th>
                <th className="w-[17.6%]"></th>
              </tr>
            </thead>
            <tbody className="text-sm bg-white">
              {auditTrail?.records?.map((trail, i) => (
                <tr className="border-[0.5px] border-[#E4E3EC]" key={i}>
                  <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-sm text-body whitespace-nowrap">
                    {trail?.actor_name || '-'}
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap capitalize">
                    {trail?.action
                      .replace(/user\./g, '')
                      .replace(/\./g, ' ')
                      .replace(/([a-z])([A-Z])/g, '$1 $2') || '-'}
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                    {moment(trail?.createdAt).format('Do MMM, YYYY, hh:mmA') ||
                      '-'}
                  </td>
                  <td className="p-4 pl-0 whitespace-nowrap">
                    <button
                      onClick={() => {
                        setOpen(true);
                        setAuditData(trail);
                      }}
                      title="View"
                      className="h-4 aspect-square"
                    >
                      <img src={eye} alt="View" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableLayout>

          {!loading && auditTrail?.records?.length !== 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
              <PageNumber
                currentPage={+currentPage}
                limit={limit}
                totalCount={auditTrail?.totalRecords || 0}
              />
              <div className="flex items-center gap-5">
                <PageLimit
                  onLimitChange={newLimit => {
                    setLimit(newLimit);
                    setCurrentPage(1);
                  }}
                  total={auditTrail?.totalRecords}
                  length={auditTrail?.records?.length}
                  limit={limit}
                />
                <Pagination
                  total={Math.ceil((auditTrail?.totalRecords || 0) / limit)}
                  current={+currentPage}
                  onPageChange={activePage => {
                    pageClick(activePage);
                  }}
                />
              </div>
            </div>
          )}

          <PrimaryButton
            buttonText="Download"
            className="block w-full mt-6 sm:hidden"
            disabled={mAppLoading || !canDownloadAudit}
            onClick={() => {
              const apiParams = parseUrlParams();
              apiParams.limit = limit;
              exportAuditLogs(apiParams.user);
            }}
            loading={mAppLoading}
          />
        </>
      )}
    </LogsLayout>
  );
}

export default connect(state => state, Actions)(AuditLogs);
